/** @format */

import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
} from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { CountdownTimer } from '../common/custom/CountDown';
import '../features/home/home.css';
import {
  fetchApplicationDatesThunk,
  selectHome,
} from '../features/home/homeSlice';
import { getBannerImgSrcFromPayload } from '../features/home/bannerUrls';
import { ApplicationSetDatesDto } from '../features/home/models';

/** Fallback when API has no custom URL configured */
const DEFAULT_HOME_BANNER =
  'https://thuthukaprod.blob.core.windows.net/banners/2025.jpg';

function Home() {
  const navigator = useNavigate();
  const [terms, setTerms] = useState(false);
  const dispatch = useAppDispatch();
  const homeSelect = useAppSelector(selectHome);
  const [data, setData] = useState<ApplicationSetDatesDto | null>(null);
  const [bannerSrc, setBannerSrc] = useState(DEFAULT_HOME_BANNER);

  useEffect(() => {
    dispatch(fetchApplicationDatesThunk()).then((resp) => {
     
    });
  }, []);

  useEffect(() => {
    const resp = homeSelect.applicationDatesState.applicationDates;
    if (resp) {
      setData(resp);
    }
  }, [homeSelect.applicationDatesState.applicationDates]);

  useEffect(() => {
    const fromApi = data ? getBannerImgSrcFromPayload(data) : '';
    setBannerSrc(fromApi || DEFAULT_HOME_BANNER);
  }, [data]);

  const displayApplicationInformation = () => {
    if (data && !data.areApplicationsClosed) {
      return true;
    }
    return false;
  };

  return (
    <>
      <Box className='banner'>
        <img
          src={bannerSrc}
          alt='Thuthuka bursary'
          className='banner_image'
          onError={() => {
            setBannerSrc((prev) =>
              prev === DEFAULT_HOME_BANNER ? prev : DEFAULT_HOME_BANNER
            );
          }}
        />
      </Box>
      <Box>
        {data && data?.showCountDown && data.closingDate && (
          <CountdownTimer targetDate={data.closingDate} />
        )}
      </Box>
      <Box className='home_content'>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <h2 className='only__open__ca'>
              Thuthuka bursary is only open to students who want to become
              Chartered Accountant
            </h2>
          </Grid>
          <Grid item xs={12}>
            <Box className='terms__condition__header'>Important Notice</Box>
            <Divider />

            <p>
              This document is an application only. It is not in any way an
              agreement or commitment for funding.
            </p>

            <p>
              Successful applicants will be required to sign a contract with
              both Thuthuka Education and Upliftment Fund (TEUF) and National
              Student Financial Aid Scheme (NSFAS) for the purpose of being
              awarded a bursary.
            </p>

            <p>
              Thuthuka Bursary cannot be used to pay for existing loans or
              debts.
            </p>

            <p>
              Applicants will be considered according to academic performance,
              National Benchmark Test (NBT) results and financial need as
              determined by the NSFAS financial means test.
            </p>
          </Grid>

          <Grid item xs={12}>
            {displayApplicationInformation() && (
              <p>
                The funding application is for the{' '}
                {new Date().getFullYear() + 1} academic year.
              </p>
            )}
            <p></p>
            Your application remains incomplete if:
            <ul>
              <li>The consent form is not signed or</li>
              <li>All required documents are not uploaded.</li>
            </ul>
            <p>All fields marked in red are compulsory</p>
            <p>Your application is not submitted until you press “SUBMIT”</p>
            <p>
              <b>
                {displayApplicationInformation() && (
                  <i>{`The closing date for the applications is ${moment(
                    data?.closingDate
                  ).format('MMMM Do YYYY, h:mm:ss a')}`}</i>
                )}
              </b>
            </p>
          </Grid>

          <Grid item xs={12} mt={3}>
            <Box className='terms__condition__header'>Supporting Documents</Box>
            <Divider />

            <ul>
              <li>
                Signed consent form (
                <a
                  target='_blank'
                  href='https://thuthukaprod.blob.core.windows.net/documents/Consent  Form.pdf'>
                  download it here
                </a>
                )
              </li>
              <li>Applicant's ID</li>
              <li>Parent(s)’/Guardian(s)’ ID</li>
              <li>Parent(s)’/Guardian(s)’ salary slip</li>
              <li>
                Copies of ID documents of persons dependent on family income
              </li>
              <li>Final Grade 11 results</li>
              <li>Final Grade 12 results (if completed Grade 12)</li>
              <li>Full university transcript (if already at University)</li>
              <li>
                University provisional acceptance letter (if you are still in
                high school).
              </li>
            </ul>
          </Grid>

          <Grid item xs={12} mt={3}>
            <Box className='terms__condition__header'>POPI Information</Box>
            <Divider />

            <p>
              <i>
                TEUF respects your privacy and acknowledge that by submitting
                your documentation that such will include personal information,
                which may belong to you, your company and/or others. By
                submitting your documentation, you are expressly submitting
                your, your company and/or others personal information to be
                processed by TEUF, which will be done in accordance with the
                Protection of Personal Information Act, 4 of 2013 (POPI) and/or
                other relevant data protection laws and regulations. For our
                Personal Information Protection Notice and related data privacy
                policies refer to our{' '}
                <a
                  target='_blank'
                  href='https://www.saica.org.za/thuthuka-education-upliftment-fund'>
                  website
                </a>
              </i>
            </p>
          </Grid>

          {displayApplicationInformation() && (
            <Grid item xs={12} mt={3}>
              <Box>
                <FormControlLabel
                  required
                  value={terms}
                  onChange={() => setTerms((prev) => !prev)}
                  control={<Checkbox />}
                  label='Please confirm you have read the above'
                />
              </Box>
              <Button
                disabled={!terms}
                onClick={() => navigator('/application')}
                size='large'
                variant='contained'>
                Start Thuthuka Bursary Application
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
}

export default Home;
